import { z } from 'zod';

const insightSectionSchema = z.object({
  headline: z.string(),
  bullets: z.array(z.string()),
});

const monthlyRowSchema = z.object({
  advertiser: z.string(),
  month: z.string(),
  spend: z.number(),
  impressions: z.number(),
});

const channelRowSchema = monthlyRowSchema.extend({ channel: z.string() });
const publisherRowSchema = monthlyRowSchema.extend({ publisher: z.string() });

const marketRowSchema = z.object({
  month: z.string(),
  spend: z.number(),
  impressions: z.number(),
});

const mediaLandscapeSchema = z.object({
  monthly: z.array(monthlyRowSchema),
  byChannel: z.array(channelRowSchema),
  byPublisher: z.array(publisherRowSchema),
  marketTotals: z.object({
    byChannel: z.array(marketRowSchema.extend({ channel: z.string() })),
    byPublisher: z.array(marketRowSchema.extend({ publisher: z.string() })),
  }),
  // Null when the insights agent failed; the section is non-fatal on the backend.
  insights: z
    .object({
      mediaMixStrategy: insightSectionSchema,
      flightingStrategy: insightSectionSchema,
      whiteSpace: insightSectionSchema,
      generatedAt: z.string(),
      analyzedAdvertisers: z.array(z.string()),
    })
    .nullable(),
});

const pathmaticsDataSchema = z.object({
  brandName: z.string(),
  availableAdvertisers: z.array(z.string()),
  mediaLandscape: mediaLandscapeSchema,
});

export type PathmaticsInsightSection = z.infer<typeof insightSectionSchema>;
export type PathmaticsMonthlyRow = z.infer<typeof monthlyRowSchema>;
export type PathmaticsChannelRow = z.infer<typeof channelRowSchema>;
export type PathmaticsPublisherRow = z.infer<typeof publisherRowSchema>;
export type PathmaticsMediaLandscape = z.infer<typeof mediaLandscapeSchema>;
export type PathmaticsMediaInsights = PathmaticsMediaLandscape['insights'];
export type PathmaticsData = z.infer<typeof pathmaticsDataSchema>;

export type PathmaticsParseResult =
  | { status: 'absent' }
  | { status: 'invalid' }
  | { status: 'ok'; data: PathmaticsData };

/**
 * `pathmatics_data` is unconstrained jsonb, so a blob written before a shape change can still
 * violate the current contract at runtime. Parsing at the boundary keeps a bad upload from
 * throwing inside aggregation and white-screening the whole analysis page.
 *
 * Only fields this page actually renders are declared. Requiring one it never reads would turn
 * a harmless gap in an older blob into a blank section, which is the failure this parse exists
 * to prevent; zod strips the rest either way.
 */
export function parsePathmaticsData(raw: unknown): PathmaticsParseResult {
  if (raw === null || raw === undefined) return { status: 'absent' };
  const parsed = pathmaticsDataSchema.safeParse(raw);
  return parsed.success ? { status: 'ok', data: parsed.data } : { status: 'invalid' };
}
